import { useMemo, useState } from 'react';
import type { RaceConfig } from '../../types/config.ts';
import type { NormalizedResult } from '../../types/results.ts';
import type { ProviderState } from '../../types/ui.ts';
import { candidateById } from '../../lib/candidates.ts';
import { candidateColor } from '../../lib/colors.ts';
import { marginOfCandidates } from '../../lib/reconcile.ts';
import { fmtInt, fmtPercent } from '../../lib/format.ts';
import { CandidateToken } from '../common/CandidateToken.tsx';
import { SourceState } from '../common/SourceState.tsx';
import { SortHeader, type SortDir } from '../controls/SortHeader.tsx';

type GeoSortKey = 'county' | 'reporting' | 'margin' | 'votes';

interface Row {
  name: string;
  reportingPercent: number | null;
  leaderId: string | null;
  marginPct: number | null;
  totalVotes: number | null;
  decisive: boolean;
  why?: string;
}

function norm(s: string): string {
  return s
    .toLowerCase()
    .replace(/\bcharter township\b/g, '')
    .replace(/\bcounty\b/g, '')
    .trim();
}

function cmp(a: Row, b: Row, key: GeoSortKey, dir: SortDir): number {
  let d = 0;
  if (key === 'county') d = a.name.localeCompare(b.name);
  else if (key === 'reporting') d = (a.reportingPercent ?? -1) - (b.reportingPercent ?? -1);
  else if (key === 'margin') d = (a.marginPct ?? -1) - (b.marginPct ?? -1);
  else d = (a.totalVotes ?? -1) - (b.totalVotes ?? -1);
  return dir === 'asc' ? d : -d;
}

/** Per-county results, sortable, with decisive counties pinned to the top with a
 *  marker and a one-line factual "why". Decisive counties missing from the data
 *  still appear as a labeled placeholder rather than vanishing. */
export function GeographyTable({
  race,
  voteState,
  resolved,
}: {
  race: RaceConfig;
  voteState: ProviderState<NormalizedResult> | undefined;
  resolved: 'dark' | 'light';
}) {
  const [sortKey, setSortKey] = useState<GeoSortKey>('margin');
  const [dir, setDir] = useState<SortDir>('desc');

  const result = voteState?.status === 'success' ? voteState.data : null;

  const rows = useMemo<Row[]>(() => {
    if (!result) return [];
    const decisiveByName = new Map(race.decisiveCounties.map((d) => [norm(d.name), d]));
    const seen = new Set<string>();

    const dataRows: Row[] = result.counties.map((c) => {
      const m = marginOfCandidates(c.candidates, c.reportingPercent);
      const key = norm(c.name);
      seen.add(key);
      const decisive = decisiveByName.get(key);
      return {
        name: c.name,
        reportingPercent: c.reportingPercent,
        leaderId: m.leaderId,
        marginPct: m.marginPct,
        totalVotes: c.totalVotes ?? (c.candidates.reduce((s, x) => s + (x.votes ?? 0), 0) || null),
        decisive: decisive != null,
        why: decisive?.why,
      };
    });

    // Placeholder rows for decisive counties with no data yet.
    for (const d of race.decisiveCounties) {
      if (!seen.has(norm(d.name))) {
        dataRows.push({
          name: d.name,
          reportingPercent: null,
          leaderId: null,
          marginPct: null,
          totalVotes: null,
          decisive: true,
          why: d.why,
        });
      }
    }
    return dataRows;
  }, [result, race.decisiveCounties]);

  const sorted = useMemo<Row[]>(() => {
    const decisive = rows.filter((r) => r.decisive).sort((a, b) => cmp(a, b, sortKey, dir));
    const rest = rows.filter((r) => !r.decisive).sort((a, b) => cmp(a, b, sortKey, dir));
    return [...decisive, ...rest];
  }, [rows, sortKey, dir]);

  if (!result) {
    return (
      <SourceState
        label="Counties"
        kind={voteState && voteState.status === 'error' ? voteState.errorKind : null}
        loading={!voteState || voteState.status === 'loading' || voteState.status === 'idle'}
      />
    );
  }
  if (!rows.length) {
    return <p className="muted">No county detail from this source.</p>;
  }

  const onSort = (k: GeoSortKey) => {
    if (k === sortKey) setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(k);
      setDir(k === 'county' ? 'asc' : 'desc');
    }
  };

  return (
    <div className="geo-wrap">
      <table className="geo">
        <caption className="visually-hidden">
          Per-county results from {result.source}. Decisive counties are listed first.
        </caption>
        <thead>
          <tr>
            <SortHeader label="County" sortKey="county" activeKey={sortKey} direction={dir} onSort={onSort} />
            <SortHeader label="Reporting" sortKey="reporting" activeKey={sortKey} direction={dir} onSort={onSort} numeric />
            <th scope="col">Leader</th>
            <SortHeader label="Margin" sortKey="margin" activeKey={sortKey} direction={dir} onSort={onSort} numeric />
            <SortHeader label="Votes" sortKey="votes" activeKey={sortKey} direction={dir} onSort={onSort} numeric />
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => {
            const leader = candidateById(race, r.leaderId);
            return (
              <tr key={r.name} className={r.decisive ? 'geo__row--decisive' : undefined}>
                <th scope="row" className="geo__county">
                  <span className="geo__county-line">
                    {r.decisive ? (
                      <span className="geo__marker" title="Decisive county" aria-label="Decisive county">
                        ◆
                      </span>
                    ) : null}
                    <span>{r.name}</span>
                  </span>
                  {r.decisive && r.why ? <span className="geo__why dim">{r.why}</span> : null}
                </th>
                <td className="col-num tabular">{fmtPercent(r.reportingPercent, 0)}</td>
                <td>
                  {leader ? (
                    <CandidateToken candidate={leader} color={candidateColor(leader, resolved)} />
                  ) : (
                    <span className="dim">—</span>
                  )}
                </td>
                <td className="col-num tabular">
                  {r.marginPct != null ? `+${r.marginPct.toFixed(1)}` : '—'}
                </td>
                <td className="col-num tabular">{fmtInt(r.totalVotes)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
